import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Col, FloatingLabel, Row, Form } from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";

const FamilyFundForecast = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Store people data fetched by selected rows
  const [forecasts, setForecasts] = useState([]);

  // Set authorization header globally
  const AuthToken = localStorage.getItem("AuthUser");
  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  // Initialize forecasts from location state
  useEffect(() => {
    const stored = sessionStorage.getItem("selectedRows");

    if (stored) {
      const selectedIds = JSON.parse(stored); // already an array of IDs [2, 4, 5]

      axios.post(`${process.env.REACT_APP_API_URL}/family-forecast`, {
        selected_ids: selectedIds,
      })
        .then((response) => {
          const forecasts = response.data.map((person) => ({
            id: person.id,
            fsf_id: person.f_number || 0,
            familyIdentifier: person?.identifier?.name ||0,
            brickKiln: person?.brick_kiln?.brickkiln_name || 0,
            brickKilnOwnerName: person?.brick_kiln?.brickkiln_owner || 0,
            brickKilnOwnerCell: person?.brick_kiln?.brickkiln_owner_cell || 0,
            brickKilnOwnerCnic: person?.brick_kiln?.owner_cnic || 0,
            name: person.beneficiary_name || 0,
            cnic: person.cnic ||0,
            loanAmount: person?.loan?.loan_amount || 0,
            transportationAmount: person?.loan?.transportation || 0,
            businessAmount: person?.loan?.estimated_business_cost || 0,
            scholarshipAmount: person.scholarshipAmount || 0,
            partnerIncentive: person.partnerIncentive || 0,
            medical_amount: person.loan?.medical_amount || 0,
            food_ration: person.loan?.food_ration || 0,
            meal_amount: person.loan?.meal_amount || 0,
            utility_amount: person.loan?.utility_amount || 0,
            rent_amount: person.loan?.rent_amount || 0,
            affidavit_amount: person.loan?.affidavit_amount || 0,
            other: person.loan?.other || 0,
          }));

          setForecasts(forecasts);
        })
        .catch((error) => {
          console.error("Error fetching forecasts:", error);
        });

    } else {
      console.warn("No selectedRows in sessionStorage");
    }
  }, []);




  // Handle input changes in editable fields
  const handleChange = (e, id) => {
    const { name, value } = e.target;

    setForecasts((prev) =>
      prev.map((forecast) =>
        forecast.id === id ? { ...forecast, [name]: value } : forecast
      )
    );
  };

  // Confirm and delete forecast entry
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are You Sure?",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        setForecasts((prev) => prev.filter((forecast) => forecast.id !== id));
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  // Submit updated forecasts to backend
  const handleSubmit = () => {
    if (forecasts.length === 0) {
      Swal.fire({
        title: "Warning!",
        text: "Please Select At Least One Identifier To Submit Request",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    Swal.fire({
      title: "Are You Sure?",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedForecasts = forecasts.map((forecast) => ({
          people_id: forecast.id,
          scholarship_amount: forecast.scholarshipAmount,
          incentive: forecast.partnerIncentive,
          medical_amount: forecast.medical_amount,
          meal_amount: forecast.meal_amount,
          rent_amount: forecast.rent_amount,
          other: forecast.other,
          affidavit_amount: forecast.affidavit_amount,
          business_amount: forecast.businessAmount,
          transportation_amount: forecast.transportationAmount,
          food_ration: forecast.food_ration,
        }));

        axios
          .post(`${process.env.REACT_APP_API_URL}/submit-forecast`, {
            families: updatedForecasts,
          })
          .then(() => {
            Swal.fire({
              title: "Success!",
              text: "Family Fund Forecast Created Successfully.",
              icon: "success",
              confirmButtonText: "Ok",
            });
            navigate("/family-forecast");
          })
          .catch((error) => {
            console.error("Error submitting data:", error);
            Swal.fire({
              title: "Error",
              text: "Failed to submit the forecast. Please try again.",
              icon: "error",
              confirmButtonText: "Ok",
            });
          });
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">Add Family Fund Forecast</h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    home
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    Finances
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">Add Family Fund Forecast</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <p className="p-3 text-center">
            <b>
              Total {forecasts.length}{" "}
              {forecasts.length > 1 ? "families are" : "family is"} ready for
              fund forecast.
            </b>
          </p>
          <hr />
        </Col>
      </Row>

      {forecasts.map((forecast, index) => (
        <div key={forecast.id} style={{ marginBottom: "20px" }}>
          <h4 className="m-3">Family Fund Forecast {index + 1}</h4>
          <Row>
            {/* Non-editable fields */}
            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Draft Family FSF-ID" className="mb-3">
                <Form.Control
                  type="text"
                  name="fsf_id"
                  value={forecast.fsf_id}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Family Identifier" className="mb-3">
                <Form.Control
                  type="text"
                  name="familyIdentifier"
                  value={forecast.familyIdentifier}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKiln"
                  value={forecast.brickKiln}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln Owner Name" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKilnOwnerName"
                  value={forecast.brickKilnOwnerName}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln Owner Cell" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKilnOwnerCell"
                  value={forecast.brickKilnOwnerCell}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Brick Kiln Owner CNIC" className="mb-3">
                <Form.Control
                  type="text"
                  name="brickKilnOwnerCnic"
                  value={forecast.brickKilnOwnerCnic}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Beneficiary Name" className="mb-3">
                <Form.Control
                  type="text"
                  name="name"
                  value={forecast.name}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Beneficiary CNIC" className="mb-3">
                <Form.Control
                  type="text"
                  name="cnic"
                  value={forecast.cnic}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Loan Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="loanAmount"
                  value={forecast.loanAmount}
                  readOnly
                />
              </FloatingLabel>
            </Form.Group>

            {/* Editable fields */}
            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Medical Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="medical_amount"
                  value={forecast.medical_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Food Ration" className="mb-3">
                <Form.Control
                  type="text"
                  name="food_ration"
                  value={forecast.food_ration}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Meal Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="meal_amount"
                  value={forecast.meal_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Utility Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="utility_amount"
                  value={forecast.utility_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Rent Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="rent_amount"
                  value={forecast.rent_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Affidavit Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="affidavit_amount"
                  value={forecast.affidavit_amount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Other Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="other"
                  value={forecast.other}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Transportation Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="transportationAmount"
                  value={forecast.transportationAmount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Business Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="businessAmount"
                  value={forecast.businessAmount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Scholarship Amount" className="mb-3">
                <Form.Control
                  type="text"
                  name="scholarshipAmount"
                  value={forecast.scholarshipAmount}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group as={Col} md={4}>
              <FloatingLabel label="Partner Incentive" className="mb-3">
                <Form.Control
                  type="text"
                  name="partnerIncentive"
                  value={forecast.partnerIncentive}
                  onChange={(e) => handleChange(e, forecast.id)}
                />
              </FloatingLabel>
            </Form.Group>

            {/* Delete button if more than one forecast */}
            {forecasts.length > 1 && (
              <Col md={4} className="d-flex align-items-center">
                <Button
                  variant="danger"
                  onClick={() => handleDelete(forecast.id)}
                  className="mt-3"
                >
                  <MdDelete style={{ fontSize: "20px", paddingTop: 0 }} /> Remove
                </Button>
              </Col>
            )}
          </Row>
        </div>
      ))}

      <Button variant="primary" onClick={handleSubmit}>
        Submit
      </Button>
    </PageLayout>
  );
};

export default FamilyFundForecast;
